import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Sparkles,
  AlertCircle,
  X,
  ClipboardList,
} from 'lucide-react';

import { apiService, TooManyActivePlansError } from '@/api';
import { useUserStore } from '@/store/userStore';
import { Button } from '@/components/ui';
import { cn } from '@/utils/cn';

import { PlanCard, Plan, extractPlanJson } from './PlanCard';

/** ===== Types ===== */

export type PlanSeed = {
  prompt: string;
  orientation?: 'landscape' | 'portrait';
  durationSec?: number;
  assetIds?: string[];
  folderId?: string;
};

type PlanState =
  | { kind: 'creating' }
  | { kind: 'planning'; orderId: string; sessionId: string }
  | { kind: 'reviewing'; orderId: string; sessionId: string; plan: Plan }
  | { kind: 'revising'; orderId: string; sessionId: string; previousPlan: Plan }
  | { kind: 'approving'; orderId: string; sessionId: string; plan: Plan }
  | { kind: 'error'; message: string; orderId?: string };

export interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called when the user approves a plan and video generation has kicked off.
   * The parent is expected to navigate to /files (optionally highlighting the
   * order row).
   */
  onApproved: (orderId: string) => void;
  /**
   * Called when the backend rejects session creation because the user is
   * already at the active-planning-session cap. Parent should close the
   * modal (we also call onClose) and surface a toast explaining that the
   * user must approve or discard an existing plan first.
   */
  onTooManyPlans?: (limit: number) => void;
  /** Seed for a brand new plan session (required when no resumeOrderId). */
  seed?: PlanSeed;
  /**
   * Existing order id to resume. When set we skip the "creating" step and
   * fetch the latest session state + messages; whether we land in planning or
   * reviewing depends on whether a plan has already arrived.
   */
  resumeOrderId?: string;
}

/** ===== Polling config ===== */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'stopped', 'success', 'error']);

function nextDelayMs(attempt: number): number {
  const steps = [5000, 8000, 12000, 20000, 30000];
  return steps[Math.min(attempt, steps.length - 1)];
}

type PlanHit = { plan: Plan; createdAtMs: number };

/**
 * Find the most recent model message that contains a parseable plan JSON,
 * along with its created_at timestamp (ms). Timestamp is 0 when HeyGen's
 * message didn't include one — in that case callers should treat it as
 * "unknown age".
 */
function extractLatestPlanFromMessages(heygen: AnyRecord): PlanHit | null {
  if (!heygen || typeof heygen !== 'object') return null;
  const sources = [
    heygen.messages,
    heygen.conversation,
    heygen.chat_messages,
    heygen.events,
    heygen.history,
  ];
  for (const src of sources) {
    if (!Array.isArray(src) || src.length === 0) continue;
    const sorted = [...src].sort((a, b) => {
      const at = new Date(a?.created_at || a?.timestamp || 0).getTime();
      const bt = new Date(b?.created_at || b?.timestamp || 0).getTime();
      return at - bt;
    });
    for (let i = sorted.length - 1; i >= 0; i--) {
      const msg = sorted[i];
      const role = (msg?.role || '').toString().toLowerCase();
      if (role === 'user') continue;
      const text = (msg?.text || msg?.content || '').toString();
      const parsed = extractPlanJson(text);
      if (parsed) {
        const createdAtMs = new Date(msg?.created_at || msg?.timestamp || 0).getTime() || 0;
        return { plan: parsed, createdAtMs };
      }
    }
  }
  return null;
}

/**
 * Deterministic-ish fingerprint of a plan so we can detect when a revision
 * has produced a genuinely new plan vs. us just re-seeing the prior plan's
 * message in the still-updating HeyGen messages list.
 */
function planFingerprint(plan: Plan | null | undefined): string {
  if (!plan) return '';
  try {
    return JSON.stringify(plan);
  } catch {
    return '';
  }
}

/** ===== Typewriter (types each sentence, deletes, cycles) ===== */

function TypewriterText({
  sentences,
  typingSpeed = 35,
  deletingSpeed = 18,
  pauseTime = 2200,
  className = '',
}: {
  sentences: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseTime?: number;
  className?: string;
}) {
  const [sentenceIndex, setSentenceIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const current = sentences[sentenceIndex] || '';

  useEffect(() => {
    if (sentences.length === 0) return;
    if (isPaused) return;

    const tick = () => {
      if (!isDeleting) {
        if (charIndex < current.length) {
          setCharIndex((p) => p + 1);
        } else {
          setIsPaused(true);
          setTimeout(() => {
            setIsPaused(false);
            setIsDeleting(true);
          }, pauseTime);
        }
      } else {
        if (charIndex > 0) {
          setCharIndex((p) => p - 1);
        } else {
          setIsDeleting(false);
          setSentenceIndex((p) => (p + 1) % sentences.length);
        }
      }
    };

    const speed = isDeleting ? deletingSpeed : typingSpeed;
    const timer = setTimeout(tick, speed);
    return () => clearTimeout(timer);
  }, [charIndex, current, isDeleting, isPaused, pauseTime, typingSpeed, deletingSpeed, sentences.length]);

  // Reset when sentence list content changes (language switch etc.)
  useEffect(() => {
    setSentenceIndex(0);
    setCharIndex(0);
    setIsDeleting(false);
    setIsPaused(false);
  }, [sentences.join('|')]);

  return (
    <span className={className} aria-live="polite">
      {current.slice(0, charIndex)}
      <span className="inline-block w-[2px] h-[1em] align-[-0.15em] ml-0.5 bg-primary animate-pulse" />
    </span>
  );
}

/**
 * Big friendly waiting animation shown while the agent is drafting / revising
 * a plan. Combines a pulsing gradient halo, a bouncing-dots ring, and a
 * rotating typewriter of tips — replaces the old static bullet list so the
 * user has something lively to watch during the 30-90s plan wait.
 */
function PlanWaitAnimation({
  title,
  stepLabel,
  tips,
}: {
  title: string;
  stepLabel: string;
  tips: string[];
}) {
  return (
    <div className="py-4 space-y-8 animate-fade-in">
      <div className="flex flex-col items-center gap-6">
        <div className="relative w-24 h-24 flex items-center justify-center">
          {/* Outer pulsing halos */}
          <span className="absolute inset-0 rounded-full bg-primary/25 animate-ping" />
          <span
            className="absolute inset-2 rounded-full bg-primary/20 animate-ping"
            style={{ animationDelay: '0.4s' }}
          />
          <span
            className="absolute inset-4 rounded-full bg-primary/15 animate-ping"
            style={{ animationDelay: '0.8s' }}
          />
          {/* Core icon bubble */}
          <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/60 to-purple-700/60 border border-primary/40 shadow-lg shadow-primary/20 flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-white" />
          </div>
        </div>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wide">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>{stepLabel}</span>
        </div>

        <h2 className="text-xl md:text-2xl font-bold text-text-primary text-center leading-tight">
          {title}
        </h2>
      </div>

      {/* Typewriter tips */}
      <div className="min-h-[4.5rem] max-w-lg mx-auto px-2">
        <p className="text-center text-base md:text-[17px] leading-relaxed text-text-secondary">
          <TypewriterText sentences={tips} />
        </p>
      </div>

      {/* Bouncing dots to reinforce "still working" */}
      <div className="flex justify-center items-center gap-1.5" aria-hidden="true">
        <span
          className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce"
          style={{ animationDelay: '0ms' }}
        />
        <span
          className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce"
          style={{ animationDelay: '150ms' }}
        />
        <span
          className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce"
          style={{ animationDelay: '300ms' }}
        />
      </div>
    </div>
  );
}

/** ===== PlanModal ===== */

export function PlanModal({
  isOpen,
  onClose,
  onApproved,
  onTooManyPlans,
  seed,
  resumeOrderId,
}: PlanModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId, user } = useUserStore();

  const [state, setState] = useState<PlanState>({ kind: 'creating' });

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAttemptRef = useRef(0);
  const resourceCacheRef = useRef<Map<string, Record<string, unknown>>>(new Map());
  const activeOrderIdRef = useRef<string | null>(null);
  /**
   * Guards `startNewSession` / `resumeSession` against double-invocation per
   * open cycle. React 18 Strict Mode intentionally re-runs effects on mount,
   * which — without this guard — would hit the HeyGen create endpoint twice
   * and leave an orphan session on HeyGen's side. Reset whenever the modal
   * closes so the next open can kick off a fresh session.
   */
  const kickedOffRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const stateRef = useRef<PlanState>(state);
  /**
   * Epoch ms captured when we send a revision message to HeyGen. Used in the
   * poll loop to make sure we only transition back to `reviewing` once a plan
   * MESSAGE newer than the revision has arrived — handles the case where the
   * agent happens to emit the same JSON twice (same fingerprint).
   */
  const revisionSentAtRef = useRef<number>(0);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const cancelPoll = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const fetchSession = useCallback(async (orderId: string) => {
    if (!userId) throw new Error('not_authenticated');
    return apiService.getHeygenChatSession(userId, orderId);
  }, [userId]);

  const schedulePoll = useCallback((orderId: string, sessionId: string) => {
    cancelPoll();
    pollTimeoutRef.current = setTimeout(async () => {
      if (!isOpenRef.current) return;
      if (activeOrderIdRef.current !== orderId) return;
      try {
        const data = await fetchSession(orderId);
        const hit = extractLatestPlanFromMessages(data.heygen);
        const heygenStatus = (data.heygen?.status || '').toString().toLowerCase();
        const localStatus = (data.local?.order_status || '').toString().toLowerCase();
        const isTerminal = TERMINAL_STATUSES.has(heygenStatus) || TERMINAL_STATUSES.has(localStatus);

        if (hit) {
          const { plan, createdAtMs } = hit;
          const newFp = planFingerprint(plan);
          const current = stateRef.current;

          // Already approving? Don't regress and don't keep polling.
          if (current.kind === 'approving') return;

          // Mid-revision: the new poll's "latest plan" might still be the
          // *previous* plan because HeyGen hasn't emitted the revised JSON
          // yet. Only transition out of revising when EITHER (a) the plan
          // content differs, OR (b) we have a message newer than when we
          // sent the revision. This handles both fresh-JSON and degenerate
          // agent-returns-the-same-JSON cases.
          if (current.kind === 'revising') {
            const prevFp = planFingerprint(current.previousPlan);
            const sameFp = newFp === prevFp;
            const isNewer =
              revisionSentAtRef.current > 0 &&
              createdAtMs > revisionSentAtRef.current;
            if (sameFp && !isNewer) {
              pollAttemptRef.current += 1;
              schedulePoll(orderId, sessionId);
              return;
            }
          }

          // Halt is now the session-poll lambda's responsibility — it scans
          // the same messages we're scanning here and fires /stop before
          // responding, keyed by plan fingerprint on the order row. So by
          // the time we see this plan in the poll response, the agent has
          // already been (or is about to be) halted server-side.
          setState((prev) => {
            if (prev.kind === 'approving') return prev;
            return { kind: 'reviewing', orderId, sessionId, plan };
          });
          return;
        }

        if (isTerminal) {
          setState({
            kind: 'error',
            message: t('plan.modal.parseErrorSubtitle'),
            orderId,
          });
          return;
        }

        pollAttemptRef.current += 1;
        schedulePoll(orderId, sessionId);
      } catch (err) {
        console.warn('Plan poll failed:', err);
        pollAttemptRef.current += 1;
        schedulePoll(orderId, sessionId);
      }
    }, nextDelayMs(pollAttemptRef.current));
  }, [cancelPoll, fetchSession, t]);

  /** Fresh-session kick-off. Runs once per open when no resumeOrderId. */
  const startNewSession = useCallback(async () => {
    if (!userId || !seed) {
      setState({ kind: 'error', message: t('plan.errors.createFailed') });
      return;
    }
    setState({ kind: 'creating' });
    pollAttemptRef.current = 0;
    try {
      const files = (seed.assetIds || []).map((id) => ({ asset_id: id }));
      const res = await apiService.createHeygenChatSession(userId, seed.prompt, {
        orientation: seed.orientation,
        files: files.length > 0 ? files : undefined,
        folderId: seed.folderId,
      });
      activeOrderIdRef.current = res.orderId;
      setState({ kind: 'planning', orderId: res.orderId, sessionId: res.sessionId });
      schedulePoll(res.orderId, res.sessionId);
    } catch (err) {
      // Backend capped us at MAX_ACTIVE_PLANS_PER_USER. Hand off to the
      // parent (FilesPage) which closes the modal and shows a toast telling
      // the user to finish or discard an existing plan.
      if (err instanceof TooManyActivePlansError) {
        onTooManyPlans?.(err.limit);
        onClose();
        return;
      }
      console.error('Failed to create plan session:', err);
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : t('plan.errors.createFailed'),
      });
    }
  }, [userId, seed, schedulePoll, t, onClose, onTooManyPlans]);

  /** Resume an existing order — skip the create call and jump to fetch. */
  const resumeSession = useCallback(async (orderId: string) => {
    if (!userId) {
      setState({ kind: 'error', message: t('plan.errors.notFound') });
      return;
    }
    setState({ kind: 'creating' });
    pollAttemptRef.current = 0;
    activeOrderIdRef.current = orderId;
    try {
      const data = await fetchSession(orderId);
      const sessionId = (data.local?.heygen_session_id || '').toString();
      if (!sessionId) {
        setState({ kind: 'error', message: t('plan.errors.notFound'), orderId });
        return;
      }
      const hit = extractLatestPlanFromMessages(data.heygen);
      if (hit) {
        // Halt is handled server-side by heygen-chat-session on its next
        // poll (or already happened on an earlier poll). No frontend halt.
        setState({ kind: 'reviewing', orderId, sessionId, plan: hit.plan });
      } else {
        setState({ kind: 'planning', orderId, sessionId });
        schedulePoll(orderId, sessionId);
      }
    } catch (err) {
      console.error('Failed to resume plan session:', err);
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : t('plan.errors.notFound'),
        orderId,
      });
    }
  }, [userId, fetchSession, schedulePoll, t]);

  /** Kick-off when modal opens; cleanup when it closes. */
  useEffect(() => {
    if (!isOpen) {
      cancelPoll();
      activeOrderIdRef.current = null;
      resourceCacheRef.current.clear();
      pollAttemptRef.current = 0;
      revisionSentAtRef.current = 0;
      kickedOffRef.current = false;
      return;
    }
    // Strict Mode double-invoke protection: only kick off a session once
    // per open cycle, no matter how many times this effect fires.
    if (kickedOffRef.current) return;
    kickedOffRef.current = true;
    if (resumeOrderId) {
      resumeSession(resumeOrderId);
    } else {
      startNewSession();
    }
    return () => {
      cancelPoll();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resumeOrderId]);

  /** Cleanup on unmount. */
  useEffect(() => () => cancelPoll(), [cancelPoll]);

  /** ===== Handlers ===== */

  const handleRequestChanges = useCallback(async (message: string) => {
    if (!userId) return;
    if (state.kind !== 'reviewing' && state.kind !== 'revising') return;
    const { orderId, sessionId } = state;
    const previousPlan = state.kind === 'reviewing' ? state.plan : state.previousPlan;
    // Stamp BEFORE the request so it's a lower bound on any new messages
    // the agent will emit in response.
    revisionSentAtRef.current = Date.now();
    setState({ kind: 'revising', orderId, sessionId, previousPlan });
    pollAttemptRef.current = 0;
    try {
      // Send the revision prompt as-is — no prefix / system wrapping. The
      // session's original system instruction still governs format (JSON
      // plan block + approval gate), so HeyGen will emit a new plan.
      await apiService.sendHeygenChatMessage(userId, orderId, message);
      schedulePoll(orderId, sessionId);
    } catch (err) {
      console.error('Failed to send revision:', err);
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : t('plan.errors.reviseFailed'),
        orderId,
      });
    }
  }, [userId, state, schedulePoll, t]);

  const handleApprove = useCallback(async (durationSec: number) => {
    if (!userId) return;
    if (state.kind !== 'reviewing') return;
    const { orderId, sessionId, plan } = state;
    setState({ kind: 'approving', orderId, sessionId, plan });
    cancelPoll();
    try {
      await apiService.sendHeygenChatMessage(userId, orderId, '', {
        autoProceed: true,
        durationSec,
      });
      onApproved(orderId);
    } catch (err) {
      console.error('Failed to approve plan:', err);
      const msg = err instanceof Error ? err.message : String(err);
      // 402 Payment Required / explicit insufficient-credits hints should
      // send the user to the paywall, matching how v1's HomePage handles the
      // same error. We leave the plan session alive (no halt/discard) so the
      // user can resume from /files after topping up.
      const lower = msg.toLowerCase();
      if (
        lower.includes('402')
        || lower.includes('insufficient_credits')
        || lower.includes('insufficient credits')
        || /insufficient/i.test(msg)
      ) {
        // No inline paywall_shown — the pricing page fires the impression on
        // arrival (with this context), so the encounter isn't counted twice.
        // Drop out of the approving state so the button doesn't stay stuck
        // in a loading state if the user hits back.
        setState({ kind: 'reviewing', orderId, sessionId, plan });
        navigate('/subscription?context=insufficient_credits');
        return;
      }
      setState({
        kind: 'error',
        message: msg || t('plan.errors.approveFailed'),
        orderId,
      });
    }
  }, [userId, state, cancelPoll, onApproved, navigate, t]);

  /**
   * User clicked the X (or pressed Escape). Close silently — the order stays
   * as `chat_active` and remains visible on /files, so the user can re-open
   * the plan card from there. The only guard is that we don't close while an
   * approve is in flight (the backend call is racing).
   */
  const requestClose = useCallback(() => {
    if (state.kind === 'approving') return;
    onClose();
  }, [state, onClose]);

  /** ===== Render ===== */

  if (!isOpen) return null;

  const tips = [
    t('plan.modal.waitTip1'),
    t('plan.modal.waitTip2'),
    t('plan.modal.waitTip3'),
    t('plan.modal.waitTip4'),
    t('plan.modal.waitTip5'),
  ];

  const content = (() => {
    switch (state.kind) {
      case 'creating':
        return (
          <PlanWaitAnimation
            title={t('plan.modal.createTitle')}
            stepLabel={t('plan.modal.creatingStep')}
            tips={tips}
          />
        );
      case 'planning':
        return (
          <PlanWaitAnimation
            title={t('plan.modal.createTitle')}
            stepLabel={t('plan.modal.planningStep')}
            tips={tips}
          />
        );
      case 'revising':
        return (
          <PlanWaitAnimation
            title={t('plan.modal.revisingStep')}
            stepLabel={t('plan.modal.revisingStep')}
            tips={tips}
          />
        );
      case 'reviewing':
      case 'approving': {
        const approving = state.kind === 'approving';
        return (
          <div className="animate-fade-in">
            <PlanCard
              plan={state.plan}
              userId={userId!}
              orderId={state.orderId}
              resourceCache={resourceCacheRef.current}
              userCredits={user ? user.remainingCredit : undefined}
              onApprove={handleApprove}
              onRequestChanges={handleRequestChanges}
              isApproving={approving}
              isRevising={false}
              disabled={approving}
            />
          </div>
        );
      }
      case 'error':
        return (
          <div className="py-10 space-y-6 animate-fade-in text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-status-error/10 border border-status-error/30">
              <AlertCircle className="w-6 h-6 text-status-error" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-1">
                {t('plan.modal.errorTitle')}
              </h3>
              <p className="text-sm text-text-secondary max-w-md mx-auto">
                {state.message}
              </p>
            </div>
            <div className="flex items-center justify-center gap-2">
              {seed && (
                <Button onClick={startNewSession} size="md">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('plan.modal.tryAgain')}
                </Button>
              )}
              <Button onClick={onClose} variant="secondary" size="md">
                {t('plan.modal.close')}
              </Button>
            </div>
          </div>
        );
    }
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/*
        Backdrop is intentionally NOT a close target — the plan is precious
        (up to ~90s of agent time) so we protect the user from accidentally
        losing it on a stray click. Closing goes through the X button
        (which triggers requestClose → confirm dialog).
      */}
      <div className="absolute inset-0 bg-black/70 animate-fade-in pointer-events-none" />
      <div
        className={cn(
          'relative w-full bg-surface shadow-xl animate-slide-up',
          'border border-glass-border',
          'rounded-t-2xl sm:rounded-2xl',
          'max-h-[92vh] sm:max-h-[90vh]',
          'mx-0 sm:mx-4',
          'flex flex-col',
          'max-w-3xl',
        )}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-glass-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h2 className="text-base sm:text-lg font-semibold text-text-primary">
              {resumeOrderId
                ? t('plan.modal.resumeTitle')
                : t('plan.modal.createTitle')}
            </h2>
          </div>
          <button
            onClick={requestClose}
            disabled={state.kind === 'approving'}
            className="p-2 rounded-lg hover:bg-surface-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={t('plan.modal.close')}
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 min-h-0">
          {content}
        </div>
      </div>
    </div>
  );
}
